<template>
  <Teleport to="body">
    <Transition name="ni-lightbox-fade">
      <div
        v-if="visible"
        class="ni-lightbox ni-lightbox--open"
        role="dialog"
        aria-modal="true"
        :aria-label="sectionTitle"
      >
        <div class="ni-lightbox__layout">
          <div class="ni-lightbox__main">
            <button
              type="button"
              class="ni-lightbox__close"
              :aria-label="t('newIndex.sections.photoDepot.exitPreview')"
              @click.stop="close"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M18 6L6 18M6 6l12 12"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </button>

            <button
              v-if="hasMultipleImages"
              type="button"
              class="ni-lightbox__nav ni-lightbox__nav--prev"
              :aria-label="t('newIndex.sections.photoDepot.prevImage')"
              @click.stop="slidePrev"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M15 18l-6-6 6-6"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>

            <div
              ref="viewportRef"
              class="ni-lightbox__viewport"
              :class="{ 'ni-lightbox__viewport--dragging': isDragging }"
              @pointerdown="handlePointerDown"
              @pointermove="handlePointerMove"
              @pointerup="handlePointerUp"
              @pointercancel="handlePointerCancel"
              @wheel.prevent="handleWheel"
            >
              <div
                ref="trackRef"
                class="ni-lightbox__track"
                :class="{
                  'ni-lightbox__track--multi': hasMultipleImages,
                  'ni-lightbox__track--animate': trackAnimate,
                }"
                :style="trackStyle"
              >
                <div
                  v-if="hasMultipleImages"
                  class="ni-lightbox__slide"
                >
                  <img
                    v-if="prevSlideUrl"
                    :src="prevSlideUrl"
                    :alt="prevImage?.title"
                    class="ni-lightbox__image"
                    draggable="false"
                    oncontextmenu="return false"
                  />
                </div>

                <div class="ni-lightbox__slide">
                  <img
                    v-if="currentSlideUrl"
                    :src="currentSlideUrl"
                    :alt="currentImage?.title"
                    class="ni-lightbox__image"
                    draggable="false"
                    oncontextmenu="return false"
                  />
                  <div v-else class="ni-lightbox__image-loading">
                    {{ t('newIndex.sections.photoDepot.loadingDetail') }}
                  </div>
                </div>

                <div
                  v-if="hasMultipleImages"
                  class="ni-lightbox__slide"
                >
                  <img
                    v-if="nextSlideUrl"
                    :src="nextSlideUrl"
                    :alt="nextImage?.title"
                    class="ni-lightbox__image"
                    draggable="false"
                    oncontextmenu="return false"
                  />
                </div>
              </div>
            </div>

            <button
              v-if="hasMultipleImages"
              type="button"
              class="ni-lightbox__nav ni-lightbox__nav--next"
              :aria-label="t('newIndex.sections.photoDepot.nextImage')"
              @click.stop="slideNext"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M9 18l6-6-6-6"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>

            <div v-if="hasMultipleImages" class="ni-lightbox__counter">
              {{ t('newIndex.sections.photoDepot.imageCounter', {
                current: activeIndex + 1,
                total: images.length,
              }) }}
            </div>

            <p v-if="hasMultipleImages" class="ni-lightbox__drag-hint">
              {{ t('newIndex.sections.photoDepot.dragHint') }}
            </p>
          </div>

          <aside class="ni-lightbox__sidebar">
            <div class="ni-lightbox__sidebar-top">
              <div class="ni-lightbox__header">
                <h3 class="ni-lightbox__title">
                  {{ picDetail?.title || currentImage?.title || sectionTitle }}
                </h3>
                <p v-if="subjectTitle" class="ni-lightbox__category">
                  {{ subjectTitle }}
                </p>
              </div>

              <div v-if="sectionDescription" class="ni-lightbox__info-box">
                <h4 class="ni-lightbox__info-label">
                  {{ t('newIndex.sections.photoDepot.sectionIntro') }}
                </h4>
                <p class="ni-lightbox__info-text">
                  {{ sectionDescription }}
                </p>
              </div>

              <div class="ni-lightbox__info-box">
                <h4 class="ni-lightbox__info-label">
                  {{ t('newIndex.sections.photoDepot.imageMeta') }}
                </h4>

                <div v-if="detailLoading && !picDetail" class="ni-lightbox__loading">
                  {{ t('newIndex.sections.photoDepot.loadingDetail') }}
                </div>

                <div v-else-if="detailRows.length" class="ni-lightbox__meta-list">
                  <div
                    v-for="row in detailRows"
                    :key="row.key"
                    class="ni-lightbox__meta-row"
                  >
                    <span class="ni-lightbox__meta-label">{{ row.label }}</span>
                    <span class="ni-lightbox__meta-value">{{ row.value }}</span>
                  </div>
                </div>

                <p v-else class="ni-lightbox__info-text ni-lightbox__info-text--muted">
                  {{ currentImage?.title || sectionTitle }}
                </p>
              </div>

              <div v-if="keywordList.length" class="ni-lightbox__keywords">
                <span class="ni-lightbox__kw-label">{{ t('pic.tip18') }}</span>
                <div class="ni-lightbox__kw-tags">
                  <span
                    v-for="(keyword, index) in keywordList"
                    :key="`${keyword}-${index}`"
                    class="ni-lightbox__kw-tag"
                  >
                    {{ keyword }}
                  </span>
                </div>
              </div>
            </div>

            <div class="ni-lightbox__footer">
              <button
                type="button"
                class="ni-lightbox__action-btn ni-lightbox__action-btn--primary"
                @click="goDetail"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M13.8 12H3"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <span>{{ t('newIndex.sections.photoDepot.viewPicDetail') }}</span>
              </button>
            </div>
          </aside>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import {
  computed,
  ref,
  watch,
  onUnmounted,
  onMounted,
  nextTick,
} from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { buildPhotoDetailUrl } from '@/utils/seoUrl.js';
import { PicVideoDetail } from '@/api/common';

const DRAG_COMMIT_RATIO = 0.18;
const VELOCITY_THRESHOLD = 0.45;

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  images: {
    type: Array,
    default: () => [],
  },
  sectionTitle: {
    type: String,
    default: '',
  },
  sectionDescription: {
    type: String,
    default: '',
  },
  initialIndex: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['close']);

const router = useRouter();
const { t } = useI18n();

const viewportRef = ref(null);
const trackRef = ref(null);
const activeIndex = ref(0);
const viewportWidth = ref(0);
const dragOffset = ref(0);
const trackAnimate = ref(false);
const isDragging = ref(false);
const detailLoading = ref(false);
const detailCache = ref({});
const picDetail = ref(null);

const dragStartX = ref(0);
const dragStartOffset = ref(0);
const dragPointerId = ref(null);
const dragStartTime = ref(0);
const lastMoveX = ref(0);
const lastMoveTime = ref(0);

let detailRequestId = 0;
let wheelLocked = false;
let resizeObserver = null;

const currentImage = computed(() => props.images[activeIndex.value] || null);
const hasMultipleImages = computed(() => props.images.length > 1);

const prevIndex = computed(() =>
  hasMultipleImages.value
    ? (activeIndex.value - 1 + props.images.length) % props.images.length
    : activeIndex.value
);
const nextIndex = computed(() =>
  hasMultipleImages.value
    ? (activeIndex.value + 1) % props.images.length
    : activeIndex.value
);

const prevImage = computed(() => props.images[prevIndex.value] || null);
const nextImage = computed(() => props.images[nextIndex.value] || null);

function getWatermarkUrl(image) {
  return image?.watermark || '';
}

const currentSlideUrl = computed(() => getWatermarkUrl(currentImage.value));
const prevSlideUrl = computed(() => getWatermarkUrl(prevImage.value));
const nextSlideUrl = computed(() => getWatermarkUrl(nextImage.value));

const trackStyle = computed(() => {
  if (!hasMultipleImages.value) {
    return { transform: 'translate3d(0, 0, 0)' };
  }

  const width = viewportWidth.value || 0;
  const base = width ? -width : 0;
  return {
    transform: `translate3d(${base + dragOffset.value}px, 0, 0)`,
  };
});

const subjectTitle = computed(() => picDetail.value?.subject?.title || props.sectionTitle || '');
const keywordList = computed(() => {
  const keywords = picDetail.value?.keywords;
  return Array.isArray(keywords) ? keywords.filter(Boolean) : [];
});

const detailRows = computed(() => {
  const detail = picDetail.value;
  if (!detail) return [];

  const rows = [];
  const pushRow = (key, label, value) => {
    if (value === undefined || value === null || value === '') return;
    rows.push({ key, label, value });
  };

  pushRow('id', t('pic.tip9'), detail.id);
  pushRow('views', `${t('contentViews')}：`, detail.views ?? 0);
  pushRow('title', t('pic.tip10'), detail.title);
  if (parseFloat(detail.ratio_w) > 0 && parseFloat(detail.ratio_h) > 0) {
    pushRow('ratio', t('pic.tip11'), `${detail.ratio_w} X ${detail.ratio_h} px`);
  }
  pushRow('norms', t('pic.tip12'), detail.norms);
  pushRow('extension', t('pic.tip13'), detail.extension);
  if (detail.size > 0) {
    pushRow('size', t('pic.tip14'), `${detail.size}MB`);
  }
  pushRow('year', t('pic.tip16'), detail.year);
  pushRow('author', t('pic.tip20'), detail.camera_man_name);
  pushRow('description', t('pic.tip17'), detail.description);

  return rows;
});

function clampIndex(index) {
  if (!props.images.length) return 0;
  return Math.min(Math.max(index, 0), props.images.length - 1);
}

function formatDetailSize(size) {
  if (!size || size <= 0) return size;
  return Math.round((size / 1024 / 1024) * 100) / 100;
}

function preloadWatermark(url) {
  if (!url) return;
  const img = new Image();
  img.src = url;
}

function preloadAdjacentImages() {
  preloadWatermark(prevSlideUrl.value);
  preloadWatermark(nextSlideUrl.value);
}

function updateViewportWidth() {
  viewportWidth.value = viewportRef.value?.clientWidth || 0;
}

function setupViewportObserver() {
  resizeObserver?.disconnect();
  if (!viewportRef.value) return;

  resizeObserver = new ResizeObserver(() => {
    updateViewportWidth();
  });
  resizeObserver.observe(viewportRef.value);
  updateViewportWidth();
}

function applyCachedDetail(id) {
  picDetail.value = detailCache.value[id] || null;
}

async function loadPicDetail(id) {
  if (!id) {
    picDetail.value = null;
    return;
  }

  applyCachedDetail(id);

  if (detailCache.value[id]) {
    detailLoading.value = false;
  } else {
    detailLoading.value = true;
  }

  const requestId = ++detailRequestId;

  try {
    const data = await PicVideoDetail({ id });
    if (requestId !== detailRequestId) return;

    const formatted = {
      ...data,
      size: formatDetailSize(data.size),
    };
    detailCache.value = {
      ...detailCache.value,
      [id]: formatted,
    };
    picDetail.value = formatted;
  } catch {
    if (requestId === detailRequestId && !detailCache.value[id]) {
      picDetail.value = null;
    }
  } finally {
    if (requestId === detailRequestId) {
      detailLoading.value = false;
    }
  }
}

function waitTrackTransition() {
  return new Promise((resolve) => {
    const track = trackRef.value;
    if (!track) {
      resolve();
      return;
    }

    const done = (event) => {
      if (event.target !== track || event.propertyName !== 'transform') return;
      track.removeEventListener('transitionend', done);
      resolve();
    };

    track.addEventListener('transitionend', done);
  });
}

async function slideTo(direction) {
  if (!hasMultipleImages.value || trackAnimate.value || !viewportWidth.value) return;

  trackAnimate.value = true;
  dragOffset.value = direction === 'next' ? -viewportWidth.value : viewportWidth.value;

  await waitTrackTransition();

  trackAnimate.value = false;
  activeIndex.value =
    direction === 'next'
      ? (activeIndex.value + 1) % props.images.length
      : (activeIndex.value - 1 + props.images.length) % props.images.length;
  dragOffset.value = 0;
}

function slideNext() {
  slideTo('next');
}

function slidePrev() {
  slideTo('prev');
}

async function snapBack() {
  if (dragOffset.value === 0) return;

  trackAnimate.value = true;
  dragOffset.value = 0;
  await waitTrackTransition();
  trackAnimate.value = false;
}

function close() {
  emit('close');
}

function goDetail() {
  const image = currentImage.value;
  if (!image?.id) return;
  router.push(buildPhotoDetailUrl(image.id));
}

function handlePointerDown(event) {
  if (!hasMultipleImages.value || trackAnimate.value) return;
  if (event.pointerType === 'mouse' && event.button !== 0) return;
  if (event.target.closest('.ni-lightbox__close, .ni-lightbox__nav')) return;

  isDragging.value = true;
  dragStartX.value = event.clientX;
  dragStartOffset.value = dragOffset.value;
  dragPointerId.value = event.pointerId;
  dragStartTime.value = performance.now();
  lastMoveX.value = event.clientX;
  lastMoveTime.value = dragStartTime.value;

  viewportRef.value?.setPointerCapture?.(event.pointerId);
}

function handlePointerMove(event) {
  if (!isDragging.value || dragPointerId.value !== event.pointerId) return;

  const deltaX = event.clientX - dragStartX.value;
  dragOffset.value = dragStartOffset.value + deltaX;

  lastMoveX.value = event.clientX;
  lastMoveTime.value = performance.now();
}

async function handlePointerUp(event) {
  if (!isDragging.value || dragPointerId.value !== event.pointerId) return;

  const deltaX = event.clientX - dragStartX.value;
  const deltaTime = Math.max(performance.now() - dragStartTime.value, 1);
  const velocity = deltaX / deltaTime;

  isDragging.value = false;
  dragPointerId.value = null;
  viewportRef.value?.releasePointerCapture?.(event.pointerId);

  if (!hasMultipleImages.value || trackAnimate.value) return;

  const threshold = viewportWidth.value * DRAG_COMMIT_RATIO;

  if (deltaX <= -threshold || velocity <= -VELOCITY_THRESHOLD) {
    await slideTo('next');
    return;
  }

  if (deltaX >= threshold || velocity >= VELOCITY_THRESHOLD) {
    await slideTo('prev');
    return;
  }

  await snapBack();
}

function handlePointerCancel(event) {
  if (dragPointerId.value !== event.pointerId) return;
  isDragging.value = false;
  dragPointerId.value = null;
  snapBack();
}

function handleWheel(event) {
  if (!hasMultipleImages.value || trackAnimate.value || wheelLocked) return;

  const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
  if (Math.abs(delta) < 12) return;

  wheelLocked = true;
  window.setTimeout(() => {
    wheelLocked = false;
  }, 420);

  if (delta > 0) {
    slideNext();
  } else {
    slidePrev();
  }
}

function handleKeydown(event) {
  if (!props.visible) return;

  if (event.key === 'Escape') {
    close();
  } else if (event.key === 'ArrowLeft' && hasMultipleImages.value) {
    slidePrev();
  } else if (event.key === 'ArrowRight' && hasMultipleImages.value) {
    slideNext();
  }
}

watch(
  () => [props.visible, props.initialIndex, props.images],
  ([visible, initialIndex]) => {
    if (visible) {
      activeIndex.value = clampIndex(initialIndex);
      dragOffset.value = 0;
      trackAnimate.value = false;
      document.body.style.overflow = 'hidden';
      nextTick(() => {
        setupViewportObserver();
        preloadAdjacentImages();
      });
    } else {
      document.body.style.overflow = '';
      picDetail.value = null;
      detailLoading.value = false;
      resizeObserver?.disconnect();
    }
  },
  { immediate: true }
);

watch(
  () => [props.visible, currentImage.value?.id],
  ([visible, id]) => {
    if (visible && id) {
      loadPicDetail(id);
    }
  },
  { immediate: true }
);

watch(
  () => [activeIndex.value, props.visible],
  ([, visible]) => {
    if (visible) {
      preloadAdjacentImages();
    }
  }
);

watch(
  () => props.visible,
  (visible) => {
    if (visible) {
      window.addEventListener('keydown', handleKeydown);
    } else {
      window.removeEventListener('keydown', handleKeydown);
    }
  }
);

onMounted(() => {
  if (props.visible) {
    nextTick(setupViewportObserver);
  }
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown);
  resizeObserver?.disconnect();
  document.body.style.overflow = '';
});
</script>
